// Reads the store and produces the ranked queue of things still worth asking,
// plus the one function that applies an answer.
//
// The ranking itself lives in the engine and knows nothing about React. This
// gathers the inputs it needs, remembers which entries were waved away, and
// routes an answer to the action that already existed for it: a rating goes to
// the rating action, a clash option to the clash action, a group conflict to the
// conflict screen, a priority to the top ten, a goal or company answer to the
// saved preferences, and a taste question to the preference editor.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from './store.tsx';
import { usePreferences } from './preferences.tsx';
import type { PreferencePatch } from './preferences.tsx';
import { buildDecisionQueue, describeQueue, partitionDecisions, preferInOrder } from '../engine/decisions.ts';
import type { Decision } from '../engine/decisions.ts';
import { fmtTime } from '../engine/time.ts';

const DISMISSED_KEY = 'dismissedDecisions';
const ANSWERED_KEY = 'answeredDecisions';

export interface AppliedDecision {
  /** A route the caller should navigate to, when the answer is somewhere else. */
  goTo?: string;
}

export function useDecisionQueue(limit = 8) {
  const { state, derived, actions, storage } = useApp();
  const { savePreferences } = usePreferences();
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [answered, setAnswered] = useState<string[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let dropped = false;
    void Promise.all([storage.meta<string[]>(DISMISSED_KEY), storage.meta<string[]>(ANSWERED_KEY)]).then(
      ([storedDismissed, storedAnswered]) => {
        if (dropped) return;
        setDismissed(Array.isArray(storedDismissed) ? storedDismissed : []);
        setAnswered(Array.isArray(storedAnswered) ? storedAnswered : []);
        setReady(true);
      },
    );
    return () => {
      dropped = true;
    };
  }, [storage]);

  const nameOfSet = useCallback(
    (setId: string) => {
      const set = derived.setById.get(setId);
      return (set && derived.artistById.get(set.artistId)?.name) ?? 'that set';
    },
    [derived.setById, derived.artistById],
  );

  const { open: queue, history } = useMemo(() => {
    if (!state.ready) return { open: [], history: [] };

    const utility = new Map<string, number>();
    for (const [, dayUtility] of derived.utilityByDay) {
      for (const [setId, value] of dayUtility) utility.set(setId, value);
    }

    const rated = new Set(
      state.ratings
        .filter((r) => r.userId === state.userId && r.source === 'manual')
        .map((r) => r.artistId),
    );

    const all = buildDecisionQueue({
      artists: state.artists,
      triage: {
        days: derived.dayContexts,
        utility,
        hardLockSetIds: derived.hardLockSetIds,
        rest: derived.restByDay,
        homeStageId: 'stage-camp',
        cfg: state.config,
      },
      ratedArtistIds: rated,
      clashesByDay: derived.clashesByDay,
      conflictsByDay: derived.conflictsByDay,
      profile: state.profiles.find((p) => p.userId === state.userId),
      memberCount: state.members.length,
      nameOfSet,
      formatTime: fmtTime,
      rateLimit: limit,
      mustSee: derived.myMustSee,
      manualScores: new Map(
        state.ratings
          .filter((r) => r.userId === state.userId && r.source === 'manual')
          .map((r) => [r.artistId, r.score]),
      ),
    });

    return partitionDecisions(all, dismissed, answered);
  }, [state, derived, dismissed, answered, nameOfSet, limit]);

  const dismiss = useCallback(
    async (id: string) => {
      const next = [...new Set([...dismissed, id])].slice(-200);
      setDismissed(next);
      await storage.setMeta(DISMISSED_KEY, next);
    },
    [dismissed, storage],
  );

  const markAnswered = useCallback(
    async (id: string) => {
      const next = [...new Set([...answered, id])].slice(-200);
      setAnswered(next);
      await storage.setMeta(ANSWERED_KEY, next);
    },
    [answered, storage],
  );

  // Returns a dismissed or answered item to the open queue.
  const restore = useCallback(
    async (id: string) => {
      const nextDismissed = dismissed.filter((d) => d !== id);
      const nextAnswered = answered.filter((a) => a !== id);
      setDismissed(nextDismissed);
      setAnswered(nextAnswered);
      await storage.setMeta(DISMISSED_KEY, nextDismissed);
      await storage.setMeta(ANSWERED_KEY, nextAnswered);
    },
    [dismissed, answered, storage],
  );

  const answer = useCallback(
    async (decision: Decision, optionId: string): Promise<AppliedDecision> => {
      const option = decision.options.find((o) => o.id === optionId);
      const apply = (option?.apply ?? {}) as Record<string, unknown>;

      if (decision.type === 'rate') {
        const artistId = String(apply.artistId ?? '');
        if (apply.skip) await dismiss(decision.id);
        else if (artistId && typeof apply.score === 'number') {
          await actions.rate(artistId, apply.score);
          await markAnswered(decision.id);
        }
        return {};
      }

      if (decision.type === 'clash') {
        const spans = (apply.spans ?? []) as { setId: string; startTs: number; endTs: number }[];
        const drops = (apply.drops ?? []) as string[];
        await actions.applyClashOption(spans, drops);
        await markAnswered(decision.id);
        return {};
      }

      if (decision.type === 'priority') {
        const order = derived.myMustSee.map((m) => m.artistId);
        await actions.setTopTen(preferInOrder(order, String(apply.prefer), String(apply.over)));
        await markAnswered(decision.id);
        return {};
      }

      if (decision.type === 'group') {
        const conflictId = String(apply.conflictId ?? '');
        return { goTo: conflictId ? `/conflicts/${encodeURIComponent(conflictId)}` : '/conflicts' };
      }

      // Goal and company are answered in the card. Taste still opens the
      // preferences screen, because it is one question per genre.
      if (apply.preferences) {
        await savePreferences(apply.preferences as PreferencePatch);
        await markAnswered(decision.id);
        return {};
      }
      return { goTo: String(apply.route ?? '/preferences') };
    },
    [actions, derived.myMustSee, dismiss, markAnswered, savePreferences],
  );

  return { queue, history, ready, summary: describeQueue(queue), answer, dismiss, restore };
}
